import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Radio } from 'antd';
import styled from '@emotion/styled';
import useUploadDocument from 'vl-common/src/hooks/useUploadDocument';
import UploadDocumentDragger from 'vl-common/src/components/UploadDocument/UploadDocumentDragger';
import { infoModal, successModal, confirmationModal, errorModal } from 'vl-common/src/components/Modals';
import LetterDocuments from '@components/Documents/LetterDocuments';
import { useQuery } from '@tanstack/react-query';
import * as actions from '@actions';
import dynamic from 'next/dynamic';
import { useDispatch } from 'react-redux';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { Select } from 'ALIASED-antd';
import OtherCaseDocuments from '@components/Clinicians/Session/v2/Components/OtherCaseDocuments';

const CIMAR = dynamic(() => import('@components/Clinicians/CIMAR'));

const CASE_VIEW = {
  CURRENT: 'CURRENT',
  OTHER: 'OTHER'
} as const;

type CaseView = (typeof CASE_VIEW)[keyof typeof CASE_VIEW];

const CaseViewSwitch = styled(Radio.Group)`
  display: inline-flex;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 6px;

  .ant-radio-button-wrapper {
    display: inline-flex;
    align-items: center;
    height: 32px;
    border: none;
    background: transparent;
    box-shadow: none;
    border-radius: 6px;
    color: rgba(0, 0, 0, 0.85);
    opacity: 0.6;

    &::before {
      display: none;
    }

    &:hover {
      opacity: 1;
      color: rgba(0, 0, 0, 0.85);
    }

    &.ant-radio-button-wrapper-checked {
      background: #ffffff;
      border-radius: 6px;
      opacity: 1;
      color: rgba(0, 0, 0, 0.85);

      &:hover {
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }
`;

const Documents = () => {
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();
  const readonly = appointment.read_only || false;
  const isCompleted = appointment.appointment_completed;
  const dispatch = useDispatch();
  const [documentsRefresh, setDocumentsRefresh] = useState(0);
  const [caseView, setCaseView] = useState<CaseView>(CASE_VIEW.CURRENT);

  const { files, setFiles, uploadDoc, fileTypes, selectedFileType, setSelectedFileType, modal } = useUploadDocument({
    userId: appointment.patient_user_guid,
    appId: appointment.appointment_id.toString(),
    afterFileUpload: () => setDocumentsRefresh((refresh) => refresh + 1)
  });

  const { data: imgResultsAvailable = true } = useQuery<boolean>({
    queryKey: ['imgResultsAvailable', appointment.case_id],
    queryFn: async () => {
      const imgDetailsRes = await dispatch(actions.clinicianInvReqByCaseId(1, appointment.case_id));
      const imgResultsAvailable = !imgDetailsRes?.invreqs?.every(
        (invreq: { ireq_results_available: boolean }) => !invreq.ireq_results_available
      );
      return imgResultsAvailable;
    }
  });

  const cimar = useMemo(
    () => <CIMAR text="PACS Viewer" case_id={appointment.case_id} appt_id={appointment.appointment_id} />,
    [appointment.appointment_id, appointment.case_id]
  );

  useEffect(() => {
    if (!modal) return;
    const { type, ...rest } = modal;
    if (!type) return;

    switch (true) {
      case type === 'success':
        successModal(rest);
        break;
      case type === 'error':
        errorModal(rest);
        break;
      case type === 'confirm':
        confirmationModal(rest);
        break;
      default:
        infoModal(rest);
    }
  }, [modal]);

  return (
    <>
      <Row style={{ margin: '0px 8px 20px 20px' }}>
        <Col span={24}>
          <CaseViewSwitch
            value={caseView}
            onChange={(e) => setCaseView(e.target.value)}
            data-testid="documents-case-view-switch"
          >
            <Radio.Button value={CASE_VIEW.CURRENT} data-testid="current-case-option">
              {t('Current Case')}
            </Radio.Button>
            <Radio.Button value={CASE_VIEW.OTHER} data-testid="other-cases-option">
              {t('Other Cases')}
            </Radio.Button>
          </CaseViewSwitch>
        </Col>
      </Row>

      {caseView === CASE_VIEW.CURRENT && (
        <>
          {imgResultsAvailable && (
            <Row gutter={[0, 8]} style={{ margin: '0px 8px 20px 20px' }}>
              <Col span={24}>{cimar}</Col>
            </Row>
          )}
          <LetterDocuments
            appId={appointment.appointment_id}
            refresh={documentsRefresh}
            caseId={appointment.case_id}
            data-testid="current-case-documents-table"
          />

          <div data-testid="documents">
            <Form.Item
              className="ant-form-textspan"
              name="AttachLetter"
              label={t('Upload document')}
              labelAlign="left"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 14 }}
            >
              <Select
                options={fileTypes}
                onChange={setSelectedFileType}
                value={selectedFileType}
                style={{ width: '100%', marginBottom: '16px' }}
                placeholder="Document type&#42;"
                data-testid="upload-document-type-select"
              />
              <UploadDocumentDragger
                shouldFileBeUploadedImmediately
                disabled={isCompleted || readonly || !selectedFileType}
                customRequest={(file) => {
                  uploadDoc(file).then(() => {
                    setSelectedFileType(null);
                  });
                }}
                files={files}
                setFiles={setFiles}
              />
            </Form.Item>
          </div>
        </>
      )}
      {caseView === CASE_VIEW.OTHER && (
        <OtherCaseDocuments patientUserGuid={appointment.patient_user_guid} currentCaseId={appointment.case_id} />
      )}
    </>
  );
};

export default Documents;
